import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { useFeatures } from '@atc/features';

import { ColorSwatch, Image } from 'reaxl';

/**
 *  Generates a checkbox element
 *
 */
const CheckboxWithVisual = React.forwardRef(({
    src,
    colorValue,
    checked = false,
    label,
    onChange = () => { },
    secondaryLabel,
    image,
    ...rest
}, ref) => {
    const {
        filters: [, {
            hide_image: hideImage = false,
        }],
    } = useFeatures([
        'filters',
    ]);

    const [isChecked, setIsChecked] = useState(checked);

    useEffect(() => {
        setIsChecked(checked);
    }, [checked]);

    const handleChange = useCallback((event) => {
        event.persist();

        setIsChecked((state) => !state);
        onChange(event);
    }, [onChange]);

    const secondLabel = useMemo(() => secondaryLabel && (
        <div className="text-normal">
            {secondaryLabel}
        </div>
    ), [secondaryLabel]);

    // Helper function to extract label text safely
    const getLabelText = useCallback((labelProp) => {
        if (typeof labelProp === 'string') {
            return labelProp;
        }
        if (labelProp?.props?.label) {
            return labelProp.props.label;
        }
        return '';
    }, []);

    const colorSwatchAltText = useMemo(
        () => (colorValue ? `${getLabelText(label)} color swatch` : ''),
        [colorValue, label, getLabelText]
    );

    const imageAltText = useMemo(
        () => (src ? `${getLabelText(label)} body style` : ''),
        [src, label, getLabelText]
    );

    return (
        <label
            className="checkbox with-visual input-card-layout margin-0"
            data-cmp="checkbox"
        >
            <input
                key={label}
                ref={(input) => ref?.current.push(input)}
                type="checkbox"
                onChange={handleChange}
                checked={isChecked}
                {...rest}
            />
            <div className="input-label row align-items-center display-flex text-nowrap">
                <div>
                    {label && (
                        <div className="display-flex gap-1">
                            {label}
                        </div>
                    )}

                    {secondLabel && (
                        <div className="input-secondary-label">
                            {secondLabel}
                        </div>
                    )}
                </div>
                {colorValue && (
                    <ColorSwatch
                        colorValue={colorValue}
                        altText={colorSwatchAltText}
                    />
                )}
                {!hideImage && (image ?? (src && (
                    <Image
                        className="margin-left-auto"
                        src={src}
                        lazyload
                        alt={imageAltText}
                        width={50}
                    />
                )))}
            </div>
        </label>
    );
});

CheckboxWithVisual.displayName = 'CheckboxWithVisual';

CheckboxWithVisual.propTypes /* remove-proptypes */ = {
    /**
    * The label to display on the checkbox element
    */
    label: PropTypes.node,
    /**
     * declare that checkbox is checked or not
     */
    checked: PropTypes.bool,
    /**
     * callback for onClick event of component
     */
    onChange: PropTypes.func,
    /**
     * if there is a secondary label display it under the primary label
     */
    secondaryLabel: PropTypes.string,
    /**
    * The image component to display
    */
    image: PropTypes.node,
    /**
     * The image source URL
     */
    src: PropTypes.string,
    /**
     * The color value for the color swatch
     */
    colorValue: PropTypes.string,
};

export default React.memo(CheckboxWithVisual);
